import { IHeaderGroupParams, IHeaderParams } from '@ag-grid-community/core'

/**
 * The two header glyphs that drive inline collections.
 *
 * A collection is read in the grid as one summary cell — "No Items", or the total its lines come
 * to — and edited behind the items modal. That is one dialog per transaction, which is the thing
 * bulk exists to avoid, so the summary's header carries a glyph that lays the collection's lines
 * out as columns instead: Item 1 is every row's first line, Item 2 its second, and the plus on the
 * band asks for one more.
 *
 * Plain DOM rather than Angular components, for the same reason the cell renderers are: a header
 * component instance per column is a cost paid on every relayout.
 */

/** What the glyphs ask of the dialog. */
export interface BulkHeaderContext {
  /** Whether this collection is currently showing its lines inline. */
  expanded(collection: string): boolean
  /** Show this collection's lines inline, or put them away again. */
  toggleItems(collection: string): void
  /** One more line's worth of columns. */
  addItem(collection: string): void
  /** Close the last item's columns. The lines themselves are left alone. */
  removeItem(collection: string): void
}

export interface BulkCollectionHeaderParams extends IHeaderParams {
  /** Named to leave ag-grid's own `context` alone. */
  bulk: BulkHeaderContext
  /** The collection this column summarises. */
  collection: string
}

export interface BulkItemBandParams extends IHeaderGroupParams {
  bulk: BulkHeaderContext
  collection: string
  /** Only the last band of a collection offers the controls — at the end of the run, once. */
  last: boolean
}

/**
 * Give a glyph the whole press.
 *
 * The same rule the cell glyphs follow: ag-grid reads the two halves of a press in two places, and
 * a header's own press starts a column drag. Stopping both is what keeps the grid out of a press
 * the glyph has already answered.
 */
const claimPress = (glyph: HTMLElement, act: () => void): void => {
  glyph.addEventListener('mousedown', event => {
    event.preventDefault()
    event.stopPropagation()
    act()
  })
  glyph.addEventListener('click', event => event.stopPropagation())
}

const headerGlyph = (
  icon: string,
  title: string,
  act: () => void,
): HTMLElement => {
  const glyph = document.createElement('i')
  glyph.className = `mdi ${icon} bt-ag-head-glyph`
  glyph.title = title
  glyph.setAttribute('role', 'button')
  claimPress(glyph, act)
  return glyph
}

/**
 * The glyph every collection's header carries, in BOTH states.
 *
 * The design draws one icon — pixel for pixel the same on a collection that is laid out as columns
 * and on one that is not — so the state is carried by the tooltip and `aria-expanded` rather than by
 * a second glyph. (This used to swap an expand arrow for a collapse arrow, which the design never
 * does and which is a different icon altogether.)
 */
const EXPAND_GLYPH = 'mdi-fullscreen-exit'

/**
 * A collection column's header: its name, and the glyph that lays its lines out as columns.
 *
 * The glyph sits at the far end of the cell with the name at the start and room between them, the
 * way the design sets it — it is a control for the column rather than a suffix of its label, and
 * pinned to the edge it is in the same place on every collection however long its name is.
 *
 * The label is rendered into ag-grid's own `ag-header-cell-text`, because the required marker is a
 * pseudo-element on that class — a custom header that invented its own markup would silently drop
 * the asterisk from every required collection.
 */
export class BulkCollectionHeader {
  private gui!: HTMLElement

  init(params: BulkCollectionHeaderParams): void {
    this.gui = document.createElement('div')
    this.gui.className = 'ag-cell-label-container bt-ag-head-inline'

    const label = document.createElement('div')
    label.className = 'ag-header-cell-label'

    const text = document.createElement('span')
    text.className = 'ag-header-cell-text'
    text.textContent = params.displayName

    const open = params.bulk.expanded(params.collection)
    const toggle = headerGlyph(
      EXPAND_GLYPH,
      open ? 'Hide the items' : 'Show the items as columns',
      () => params.bulk.toggleItems(params.collection),
    )
    toggle.classList.add('bt-ag-head-glyph-end')
    toggle.setAttribute('aria-expanded', String(open))
    label.append(text, toggle)
    this.gui.appendChild(label)
  }

  getGui(): HTMLElement {
    return this.gui
  }
}

/**
 * An Item band: its name, and — on the last band of the run — the plus that adds another and the
 * cross that closes it again.
 */
export interface BulkGutterHeaderParams extends IHeaderParams {
  /** The mdi glyph this gutter reports with — the same one its cells carry. */
  icon: string
}

/**
 * A gutter column's header: the glyph its cells use, and nothing else.
 *
 * The three gutters are the frame around the record rather than fields of it, so they are named by
 * what they report rather than by a word — and the glyph is the one the cells below carry, so the
 * column reads as a column rather than as three unrelated marks.
 */
export class BulkGutterHeader {
  private gui!: HTMLElement

  init(params: BulkGutterHeaderParams): void {
    this.gui = document.createElement('div')
    this.gui.className = 'bt-ag-gutter-head-mark'

    // `headerTooltip` is rendered by ag-grid's OWN header component, and this replaces it — so a
    // colDef that declares one gets nothing unless the replacement carries it over. Read off the
    // colDef rather than passed again as a param, so the declaration stays in one place.
    const tooltip = params.column.getColDef().headerTooltip
    if (tooltip) this.gui.title = tooltip

    const glyph = document.createElement('i')
    glyph.className = `mdi ${params.icon}`
    this.gui.appendChild(glyph)
  }

  getGui(): HTMLElement {
    return this.gui
  }
}

export class BulkItemBandHeader {
  private gui!: HTMLElement

  init(params: BulkItemBandParams): void {
    this.gui = document.createElement('div')
    this.gui.className = 'ag-header-group-cell-label bt-ag-head-inline'

    const text = document.createElement('span')
    text.className = 'ag-header-group-text'
    text.textContent = params.displayName
    this.gui.appendChild(text)

    if (!params.last) return
    this.gui.append(
      headerGlyph('mdi-plus', 'Add another item', () =>
        params.bulk.addItem(params.collection),
      ),
      headerGlyph('mdi-close', 'Close the last item', () =>
        params.bulk.removeItem(params.collection),
      ),
    )
  }

  getGui(): HTMLElement {
    return this.gui
  }
}
